// @flow
import { connect, Provider } from 'react-redux';
import _includes from 'lodash/includes';
import AxiosInstanceFactory from '@src/utils/axiosInstance';

import type { RailsContextType, ObjectType } from '@types/misc';
import type { Axios } from 'axios';

import CheckoutStepOne from '@components/cart/CheckoutStepOne';
import CheckoutStepTwo from '@components/cart/CheckoutStepTwo';
import CheckoutStepThree from '@components/cart/CheckoutStepThree';
import CheckoutStepFour from '@components/cart/CheckoutStepFour';

import * as routes from '@src/app_routes/cart';

declare var Rollbar: any;
declare var Accept: any;
declare var Authorize: any;

const allSteps = ['basic', 'billing', 'shipping', 'confirm'];

type RefType = {
  current: null | React.ElementRef<'form'>
};

type ErrorFieldType = string;

type PropsType = {
  onShipToStateChange: (state: { [string]: string }) => void,
  cartTotal: number
};

type StateType = {
  activeStep: string,
  completedSteps: Array<string>,
  shipToBillingAddress: boolean,
  errorFields: Array<ErrorFieldType>,
  creditCardData: {
    cardNumber?: string,
    month?: string,
    year?: string,
    cardCode?: string,
    zip?: string
  },
  creditCardDescriptor: string,
  creditCardNonce: string,
  creditCardCachedAtMs: ?number,
  submissionErrors: ?string
};

class CheckoutSteps extends React.Component<PropsType, StateType> {
  axiosInstance: Axios;
  formRef: RefType;

  constructor(props: PropsType) {
    super(props);

    this.axiosInstance = AxiosInstanceFactory();
    this.formRef = React.createRef();
  }

  state = {
    activeStep: 'basic',
    completedSteps: [],
    shipToBillingAddress: true,
    errorFields: [],
    creditCardData: {},
    creditCardDescriptor: '',
    creditCardNonce: '',
    creditCardCachedAtMs: null,
    submissionErrors: null
  };

  handleShipToBillingChange = (e: SyntheticMouseEvent<HTMLInputElement>) => {
    this.setState({
      shipToBillingAddress: e.currentTarget.value === 'true'
    });
  };

  validateFields = fields => {
    return new Promise((resolve, reject) => {
      return this.axiosInstance
        .post(routes.validate, fields)
        .then(response => {
          const { errors } = response.data;

          if (response.status === 200 && errors.length === 0) {
            resolve();
          } else {
            reject(errors);
          }
        })
        .catch(error => {
          if (typeof Rollbar !== 'undefined') {
            Rollbar.error(
              'An error occurred while validating cart form fields',
              error
            );
          }
          window &&
            window.alert(
              'There was a problem validating your input. Please refresh the page and try again.'
            );
        });
    });
  };

  nextStep = (fieldsToValidate, allowMovementToTheNextStep = true) => {
    this.validateFields(fieldsToValidate).then(
      res => {
        if (!allowMovementToTheNextStep) {
          this.setState({ errorFields: [] });
          return;
        }

        if (this.state.activeStep === 'basic') {
          this.setState({
            activeStep: 'billing',
            completedSteps: allSteps.slice(0, 1),
            errorFields: []
          });
        } else if (this.state.activeStep === 'billing') {
          if (this.state.shipToBillingAddress) {
            this.props.onShipToStateChange(
              fieldsToValidate['order']['billing_state']
            );
          }
          this.setState({
            activeStep: this.state.shipToBillingAddress
              ? 'confirm'
              : 'shipping',
            completedSteps: allSteps.slice(0, 2),
            errorFields: []
          });
        } else if (this.state.activeStep === 'shipping') {
          this.props.onShipToStateChange(
            fieldsToValidate['order']['shipping_state']
          );

          this.setState({
            activeStep: 'confirm',
            completedSteps: allSteps.slice(0, 3),
            errorFields: [],
            submissionErrors: null
          });
        }
      },
      errorFields => {
        this.setState({ errorFields });
      }
    );
  };

  completeFinalStep = () => {
    const currentEpochMs = new Date().getTime();

    this.setState({ submissionErrors: null }, () => {
      // 840000ms is 14 minutes
      if (
        this.state.creditCardCachedAtMs != null &&
        currentEpochMs - this.state.creditCardCachedAtMs > 840000
      ) {
        this.getPaymentToken(this.state.creditCardData, this.submitForm);
      } else {
        this.submitForm();
      }
    });
  };

  editStep = (e, stepName) => {
    e.preventDefault();
    this.setState({
      activeStep: stepName,
      completedSteps: allSteps.slice(0, allSteps.indexOf(stepName))
    });
  };

  getPaymentToken = (paymentData, callback) => {
    this.setState({ creditCardData: paymentData });

    var authData = {};
    authData.clientKey = Authorize.client_key;
    authData.apiLoginID = Authorize.login;

    var secureData = {};
    secureData.authData = authData;
    secureData.cardData = paymentData;

    Accept.dispatchData(secureData, response => {
      if (response.messages.resultCode === 'Ok') {
        // Since card is valid, we want to cache it for use in the order.
        // We also want to cache the epoch when the nonce was created, because
        // the nonce is only valid for 15 minutes.
        this.setState(
          {
            creditCardDescriptor: response.opaqueData.dataDescriptor,
            creditCardNonce: response.opaqueData.dataValue,
            creditCardCachedAtMs: new Date().getTime() // cache epoch in ms
          },
          () => callback(response)
        );
      } else {
        callback(response);
      }
    });
  };

  submitForm = (_?: any) => {
    // may be called with an unused argument
    if (!this.formRef.current) return;

    return this.axiosInstance
      .post(routes.createOrder, Rails.serializeElement(this.formRef.current), {
        headers: { 'content-type': 'application/x-www-form-urlencoded' }
      })
      .then(response => {
        const { result, errors, redirect_location } = response.data;

        if (response.status === 200) {
          if (result === 'success') {
            if (window) window.location = redirect_location;
          } else {
            this.setState({ submissionErrors: errors });
          }
        } else {
          window &&
            window.alert(
              'There was a problem submitting your order. Please try again.'
            );
        }
      })
      .catch(error => {
        if (typeof Rollbar !== 'undefined') {
          Rollbar.error('An error occurred while submitting an order', error);
        }
        window &&
          window.alert(
            'There was a problem submitting your order. Please try again.'
          );
      });
  };

  render() {
    return (
      <div id='checkout_steps'>
        <form
          action='/orders/confirm'
          method='post'
          onSubmit={e => {
            e.preventDefault();
          }}
          ref={this.formRef}
        >
          <input
            type='hidden'
            name='order[credit_card_descriptor]'
            value={this.state.creditCardDescriptor}
          />
          <input
            type='hidden'
            name='order[credit_card_nonce]'
            value={this.state.creditCardNonce}
          />
          <CheckoutStepOne
            active={this.state.activeStep === 'basic'}
            completed={_includes(this.state.completedSteps, 'basic')}
            stepName='basic'
            onSubmitClick={this.nextStep}
            editStep={this.editStep}
            errorFields={this.state.errorFields}
          />
          <CheckoutStepTwo
            active={this.state.activeStep === 'billing'}
            completed={_includes(this.state.completedSteps, 'billing')}
            stepName='billing'
            onSubmitClick={this.nextStep}
            validateCardData={this.getPaymentToken}
            editStep={this.editStep}
            onShipToBillingChange={this.handleShipToBillingChange}
            errorFields={this.state.errorFields}
          />
          <CheckoutStepThree
            active={this.state.activeStep === 'shipping'}
            completed={_includes(this.state.completedSteps, 'shipping')}
            stepName='shipping'
            onSubmitClick={this.nextStep}
            editStep={this.editStep}
            displayStep={!this.state.shipToBillingAddress}
            errorFields={this.state.errorFields}
          />
          <CheckoutStepFour
            active={this.state.activeStep === 'confirm'}
            completed={_includes(this.state.completedSteps, 'confirm')}
            stepName='confirm'
            onSubmitClick={this.completeFinalStep}
            cartTotal={this.props.cartTotal}
            submissionErrors={this.state.submissionErrors}
          />
        </form>
      </div>
    );
  }
}

function mapStateToProps(state) {
  return {
    cartTotal: state.cart.totals.order_total
  };
}

function mapDispatchToProps(dispatch: Function) {
  return {
    onShipToStateChange: function(state) {
      dispatch({ type: 'SHIPPING_STATE_UPDATED', shippingState: state });
    }
  };
}

const ConnectedCheckoutSteps = connect(
  mapStateToProps,
  mapDispatchToProps
)(CheckoutSteps);

export default (props: PropsType, railsContext: RailsContextType) =>
  () => {
    const store = ReactOnRails.getStore('SharedReduxStore');

    return (
      <Provider store={store}>
        <ConnectedCheckoutSteps {...{ ...props, railsContext }} />
      </Provider>
    );
  };
